import { BookCheck, CheckCircle2, CircleHelp, Download, Pencil, Receipt, Send, Undo2, XCircle } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { confirmModal, Modal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/lib/auth/AuthProvider'
import { fmtDate, fmtDateTime, fmtMoney } from '../../../shared/format'
import { fmtSize } from '../../meetingFiles'
import { downloadExpenseFile, EXPENSE_STATUS_TONE, useCustomerExpense, useExpenseAction } from '../../expenses'
import { toastError, useCrm } from '../../ui'
import { ExpenseFormModal } from './ExpenseFormModal'
import { LegacyPostModal } from './LegacyPostModal'

/** Gider ayrıntısı: bilgiler, belgeler, süreç geçmişi; sahibi için gönder/yanıtla/geri çek, onaylayıcı için karar. */
export function ExpenseDetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation()
  const { staffName, customerName } = useCrm()
  const { user } = useAuth()
  const q = useCustomerExpense(id)
  const act = useExpenseAction()
  const [note, setNote] = useState('')
  const [editing, setEditing] = useState(false)
  const [posting, setPosting] = useState(false)
  const e = q.data
  const run = (action: 'submit' | 'respond' | 'cancel' | 'decide', decision?: 'approve' | 'reject' | 'request_info') =>
    act.mutate(
      { id, action, decision, note },
      {
        onSuccess: () => {
          toast.success(t(`crm.expenses.done.${decision ?? action}`))
          setNote('')
          if (decision === 'approve' || decision === 'reject' || action === 'cancel') onClose()
        },
        onError: toastError,
      },
    )
  const needNote = !note.trim()

  return (
    <Modal open size="xl" onOpenChange={(o) => !o && onClose()} icon={<Receipt />} title={e?.title ?? t('crm.expenses.detail')} description={e ? customerName(e.accountId) : undefined}>
      {!e ? (
        <Skeleton className="h-64" />
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={EXPENSE_STATUS_TONE[e.status]}>{t(`crm.expenses.statuses.${e.status}`)}</Badge>
            {e.legacy ? <Badge tone="neutral">{t('crm.expenses.legacy')}</Badge> : null}
            {e.legacy && e.status === 'approved' ? (
              <Badge tone={e.financeRef === 'excluded' ? 'neutral' : e.financeRef ? 'success' : 'warning'}>
                {t(e.financeRef === 'excluded' ? 'crm.expenses.legacyExcluded' : e.financeRef ? 'crm.expenses.legacyPosted' : 'crm.expenses.legacyNotPosted')}
              </Badge>
            ) : null}
            <span className="text-2xl font-semibold tabular-nums">{fmtMoney(e.amount)}</span>
          </div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
            {[
              [t('crm.expenses.spender'), staffName(e.userId)],
              [t('crm.expenses.spentOn'), fmtDate(e.spentOn)],
              [t('crm.expenses.category'), t(`crm.expenses.categories.${e.category}`)],
              [t('crm.expenses.paymentMethod'), t(`crm.expenses.methods.${e.paymentMethod}`)],
              [
                t('crm.expenses.documentType'),
                e.documentType === 'invoice' ? `${t('crm.expenses.documentTypes.invoice')}${e.invoiceNo ? ` · ${e.invoiceNo}` : ''}` : t('crm.expenses.documentTypes.receipt'),
              ],
              ...(e.documentType === 'invoice'
                ? [[t('crm.expenses.vat'), `%${Number(e.vatRate)} · ${fmtMoney(e.vatAmount)}${e.vatDeductible ? '' : ` (${t('crm.expenses.vatInExpense')})`}`]]
                : []),
              [t('crm.expenses.submittedAt'), e.submittedAt ? fmtDateTime(e.submittedAt) : '—'],
              [t('crm.expenses.decidedBy'), e.decidedBy ? staffName(e.decidedBy) : '—'],
            ].map(([k, val]) => (
              <div key={k}>
                <dt className="text-xs text-muted-foreground">{k}</dt>
                <dd className="font-medium">{val}</dd>
              </div>
            ))}
          </dl>
          {e.description ? <p className="whitespace-pre-wrap rounded-lg bg-muted/40 p-3 text-sm">{e.description}</p> : null}
          {e.status === 'approved' && e.financeRef.includes('reimbursement:') ? <p className="text-xs text-muted-foreground">{t('crm.expenses.reimbursementNote')}</p> : null}
          {e.status === 'approved' && e.financeRef.includes('advance:') ? <p className="text-xs text-muted-foreground">{t('crm.expenses.advanceNote')}</p> : null}

          <section className="space-y-1.5">
            <h3 className="text-sm font-semibold">{t('crm.expenses.receipts')}</h3>
            {e.files.length ? (
              e.files.map((f) => (
                <div key={f.id} className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
                  <Receipt className="size-4 text-muted-foreground" aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{f.name}</span>
                  <span className="text-xs text-muted-foreground">{fmtSize(f.size)}</span>
                  <Button size="icon" variant="ghost" className="size-7" aria-label={t('crm.files.download')} onClick={() => void downloadExpenseFile(e.id, f).catch(toastError)}>
                    <Download />
                  </Button>
                </div>
              ))
            ) : (
              <p className="text-xs text-muted-foreground">{t('crm.expenses.noReceipts')}</p>
            )}
          </section>

          <section className="space-y-1.5">
            <h3 className="text-sm font-semibold">{t('crm.expenses.history')}</h3>
            <ol className="space-y-1.5">
              {e.events.map((ev, i) => (
                <li key={i} className="rounded-lg border px-3 py-2 text-xs">
                  <p>
                    <span className="font-medium">{t(`crm.expenses.events.${ev.kind}`, { defaultValue: ev.kind })}</span> · {staffName(ev.userId)} ·{' '}
                    <span className="tabular-nums text-muted-foreground">{fmtDateTime(ev.createdAt)}</span>
                  </p>
                  {ev.note ? <p className="mt-1 whitespace-pre-wrap text-sm">{ev.note}</p> : null}
                </li>
              ))}
            </ol>
          </section>

          {e.canDecide || (e.canEdit && e.status === 'info_requested') ? (
            <Textarea rows={3} value={note} onChange={(x) => setNote(x.target.value)} placeholder={t(e.canDecide ? 'crm.expenses.decisionNote' : 'crm.expenses.responseNote')} />
          ) : null}
          <div className="flex flex-wrap justify-end gap-2 border-t pt-3">
            {e.canPostLegacy ? (
              <Button variant="outline" onClick={() => setPosting(true)}>
                <BookCheck />
                {t('crm.expenses.legacyPost.open')}
              </Button>
            ) : null}
            {e.canEdit ? (
              <Button variant="outline" onClick={() => setEditing(true)}>
                <Pencil />
                {t('common.edit')}
              </Button>
            ) : null}
            {e.canEdit && e.status === 'draft' ? (
              <Button onClick={() => run('submit')} disabled={act.isPending}>
                <Send />
                {t('crm.expenses.submit')}
              </Button>
            ) : null}
            {e.canEdit && e.status === 'info_requested' ? (
              <Button onClick={() => run('respond')} disabled={needNote || act.isPending}>
                <Send />
                {t('crm.expenses.respond')}
              </Button>
            ) : null}
            {user?.id === e.userId && ['draft', 'pending', 'info_requested'].includes(e.status) ? (
              <Button
                variant="ghost"
                onClick={() =>
                  void confirmModal({ title: t('crm.expenses.cancelTitle'), description: t('crm.expenses.cancelBody'), confirmLabel: t('crm.expenses.cancel'), destructive: true }).then(
                    (ok) => ok && run('cancel'),
                  )
                }
              >
                <Undo2 />
                {t('crm.expenses.cancel')}
              </Button>
            ) : null}
            {e.canDecide ? (
              <>
                {e.status === 'pending' ? (
                  <Button variant="outline" onClick={() => run('decide', 'request_info')} disabled={needNote || act.isPending}>
                    <CircleHelp />
                    {t('crm.expenses.requestInfo')}
                  </Button>
                ) : null}
                <Button variant="outline" className="text-destructive" onClick={() => run('decide', 'reject')} disabled={needNote || act.isPending}>
                  <XCircle />
                  {t('crm.expenses.reject')}
                </Button>
                {e.status === 'pending' ? (
                  <Button onClick={() => run('decide', 'approve')} disabled={act.isPending}>
                    <CheckCircle2 />
                    {t('crm.expenses.approve')}
                  </Button>
                ) : null}
              </>
            ) : null}
          </div>
          {e.canDecide ? <p className="text-right text-xs text-muted-foreground">{t('crm.expenses.decisionHint')}</p> : null}
        </div>
      )}
      {editing && e ? <ExpenseFormModal expense={e} onClose={() => setEditing(false)} /> : null}
      {posting && e ? <LegacyPostModal expense={e} onClose={() => setPosting(false)} /> : null}
    </Modal>
  )
}
